import qs from 'qs';

import {
  AWIN_CLICK_REF_STORAGE_KEY,
  AWIN_CLICK_REF_TTL_DAYS,
  AWIN_QUERY_KEY,
} from 'constants/affiliate';

const AWIN_CLICK_REF_EXPIRY = AWIN_CLICK_REF_TTL_DAYS * 24 * 60 * 60 * 1000;
const POLL_INTERVAL_MS = 100;
const CONSENT_API_MAX_ATTEMPTS = 100;
const COOKIE_WRITE_MAX_ATTEMPTS = 50;

let granted = false;

function waitFor(predicate, maxAttempts) {
  return new Promise(resolve => {
    let attempts = 0;
    function check() {
      if (predicate()) {
        resolve(true);
      } else if (attempts >= maxAttempts) {
        resolve(false);
      } else {
        attempts += 1;
        setTimeout(check, POLL_INTERVAL_MS);
      }
    }
    check();
  });
}

function readStoredClickRef() {
  try {
    const stored = JSON.parse(localStorage.getItem(AWIN_CLICK_REF_STORAGE_KEY));
    if (stored?.awc && Date.now() - stored.stored_at <= AWIN_CLICK_REF_EXPIRY) {
      return stored.awc;
    }
  } catch (err) {
    console.error('Could not restore the AWin click reference from localStorage', err);
  }
  return null;
}

function appendUrlClickRef(clickRef) {
  const params = qs.parse(window.location.search.replace(/^\?/, ''));
  if (params[AWIN_QUERY_KEY]) {
    return false;
  }
  params[AWIN_QUERY_KEY] = clickRef;
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}?${qs.stringify(params)}${window.location.hash}`,
  );
  return true;
}

function removeUrlClickRef() {
  const params = qs.parse(window.location.search.replace(/^\?/, ''));
  if (!params[AWIN_QUERY_KEY]) {
    return;
  }
  delete params[AWIN_QUERY_KEY];
  const search = qs.stringify(params);
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
  );
}

function clickRefCookiePresent(clickRef) {
  const cookieName = `_aw_m_${clickRef.split('_')[0]}`;
  return document.cookie.split('; ').includes(`${cookieName}=${clickRef}`);
}

export default async function grantAwinConsent() {
  if (granted || typeof window === 'undefined' || !window.AWIN) {
    return;
  }
  granted = true;

  const apiAvailable = await waitFor(
    () => !!window.AWIN.Tracking?.Consent?.setAdvertiserConsentStatus,
    CONSENT_API_MAX_ATTEMPTS,
  );
  if (!apiAvailable) {
    if (window.AWIN.Tracking) {
      window.AWIN.Tracking.AdvertiserConsent = true;
    }
    return;
  }
  if (window.AWIN.Tracking.Sale) {
    window.AWIN.Tracking.AdvertiserConsent = true;
    return;
  }

  const clickRef = readStoredClickRef();
  const appended = clickRef ? appendUrlClickRef(clickRef) : false;
  try {
    window.AWIN.Tracking.Consent.setAdvertiserConsentStatus(true);
    if (appended) {
      await waitFor(() => clickRefCookiePresent(clickRef), COOKIE_WRITE_MAX_ATTEMPTS);
    }
  } catch (err) {
    console.error('AWin consent grant failed', err);
  } finally {
    if (appended) {
      removeUrlClickRef();
    }
  }
}
